import React from 'react';

import {LimitItem} from 'src/components/limit-item/limit-item';
import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {WidthItem} from 'src/components/width-item';
import {MCP_TOOL_CALL_LIMIT_CODES} from 'src/constants';

import {McpTooltip} from './mcp-tooltip';

import type {OnChangeLimits, TempLimitsSettings} from 'src/modules/usage-limits/typedef';
import type {DictionaryItem} from 'src/openapi/admin/models/DictionaryItem';
import type {Limit} from 'src/openapi/admin/models/Limit';

type Props = {
    editDisabled: boolean,
    isPremiumMcpEnabled: boolean,
    mcpLimitsDictionary: DictionaryItem[],
    data: Limit[],
    tempLimitsSettings: TempLimitsSettings,
    tooltipText: string,

    getDefaultValue: (code: string) => number,
    getUsedValue?: (code: string) => number | undefined,
    onLimitChange: OnChangeLimits,
    changeTemporaryLimit: OnChangeLimits,
    clearTemporaryLimit: (limitId: string) => void,
    clearCustomLimit: (limitId: string) => void,
};

export const ConnectorLimitsList = ({
    editDisabled,
    isPremiumMcpEnabled,
    mcpLimitsDictionary,
    data,
    tempLimitsSettings,
    tooltipText,
    getDefaultValue,
    getUsedValue,
    onLimitChange,
    changeTemporaryLimit,
    clearTemporaryLimit,
    clearCustomLimit,
}: Props) => (
    <ResponsiveElementGroup>
        {mcpLimitsDictionary
            .filter(
                d =>
                    isPremiumMcpEnabled
                    || d.code !== 'MCP_PREM_M',
            )
            .map(dictItem => {
                const defaultValue =
                    getDefaultValue(dictItem.code);
                const customLimit = data.find(
                    l => l.limitId === dictItem.code,
                );
                const customValue =
                    customLimit
                    && customLimit.value
                    !== defaultValue
                        ? customLimit.value
                        : undefined;
                const tempValue =
                    tempLimitsSettings.limits.find(
                        l =>
                            l.limitId
                            === dictItem.code,
                    )?.value;

                return (
                    <WidthItem
                        key={dictItem.code}
                        width={300}>
                        <LimitItem
                            limitId={dictItem.code}
                            readonly={editDisabled}
                            limitDictionary={dictItem}
                            defaultValue={defaultValue}
                            customValue={customValue}
                            tempValue={tempValue}
                            untilDate={
                                tempLimitsSettings
                                    .expiresOn
                            }
                            usedValue={
                                getUsedValue?.(
                                    dictItem.code,
                                )
                            }
                            onClearCustomLimit={
                                clearCustomLimit
                            }
                            onClearTemporaryLimit={
                                clearTemporaryLimit
                            }
                            onChangeLimit={
                                tempLimitsSettings
                                    .expiresOn
                                    ? changeTemporaryLimit
                                    : onLimitChange
                            }
                            labelExtra={
                                MCP_TOOL_CALL_LIMIT_CODES
                                    .has(dictItem.code)
                                    ? (
                                            <McpTooltip
                                                text={
                                                    tooltipText
                                                }
                                            />
                                        )
                                    : undefined
                            }
                        />
                    </WidthItem>
                );
            })}
    </ResponsiveElementGroup>
);
